import { router } from '@inertiajs/react'
import { useState } from 'react'
import {
  SearchIcon,
  PlusIcon,
  XIcon,
  ArrowUpIcon,
  ArrowDownIcon,
} from 'lucide-react'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

interface Note {
  id: number
  content: string
  created_at: string
}

interface Lead {
  id: number
  name: string
  phone: string | null
  source: string
  source_id: string | null
  instagram_username: string | null
  messenger_username: string | null
  vehicle_type: string | null
  issue_description: string | null
  urgency: string
  sentiment: string | null
  quality: string | null
  status: string
  feedback: string | null
  notes: Note[]
  created_at: string
  updated_at: string
}

interface Filters {
  search?: string | null
  status?: string
  urgency?: string
  source?: string
  sort_by?: string
  sort_direction?: 'asc' | 'desc'
}

interface Stats {
  total: number
  new: number
  contacted: number
  estimate_requested: number
  appointment_scheduled: number
  won: number
  lost: number
}

interface LeadsIndexProps {
  leads: Lead[]
  filters: Filters
  stats: Stats
}

export default function LeadsIndex({ leads, filters, stats }: LeadsIndexProps) {
  const [searchTerm, setSearchTerm] = useState(filters.search || '')
  const [sortBy, setSortBy] = useState<string | null>(filters.sort_by || null)
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | null>(
    filters.sort_direction || null
  )
  const [editingIssueId, setEditingIssueId] = useState<number | null>(null)
  const [editingIssueValue, setEditingIssueValue] = useState('')
  const [editingNoteId, setEditingNoteId] = useState<number | null>(null)
  const [editingNoteValue, setEditingNoteValue] = useState('')
  const [editingFeedbackId, setEditingFeedbackId] = useState<number | null>(null)
  const [editingFeedbackValue, setEditingFeedbackValue] = useState('')

  // Appointment dialog state
  const [appointmentDialogOpen, setAppointmentDialogOpen] = useState(false)
  const [appointmentLeadId, setAppointmentLeadId] = useState<number | null>(null)
  const [appointmentLeadName, setAppointmentLeadName] = useState('')
  const [appointmentDate, setAppointmentDate] = useState('')
  const [appointmentTime, setAppointmentTime] = useState('')
  const [appointmentType, setAppointmentType] = useState('Diagnostic')

  const handleSearch = (search: string) => {
    router.get('/leads', {
      search: search || undefined,
      status: filters.status !== 'all' ? filters.status : undefined,
      urgency: filters.urgency !== 'all' ? filters.urgency : undefined,
      source: filters.source !== 'all' ? filters.source : undefined,
      sort_by: sortBy || undefined,
      sort_direction: sortDirection || undefined,
    }, {
      preserveState: true,
      preserveScroll: true,
    })
  }

  const handleSort = (column: string) => {
    let newSortBy: string | null = column
    let newSortDirection: 'asc' | 'desc' | null = 'asc'

    // Three-state cycle: null → asc → desc → null
    if (sortBy === column) {
      if (sortDirection === 'asc') {
        newSortDirection = 'desc'
      } else if (sortDirection === 'desc') {
        // Clear sort
        newSortBy = null
        newSortDirection = null
      }
    }

    setSortBy(newSortBy)
    setSortDirection(newSortDirection)

    router.get('/leads', {
      search: filters.search,
      status: filters.status !== 'all' ? filters.status : undefined,
      urgency: filters.urgency !== 'all' ? filters.urgency : undefined,
      source: filters.source !== 'all' ? filters.source : undefined,
      sort_by: newSortBy || undefined,
      sort_direction: newSortDirection || undefined,
    }, {
      preserveState: true,
      preserveScroll: true,
    })
  }

  const handleStatusChange = (leadId: number, leadName: string, newStatus: string) => {
    // If changing to "Appointment Scheduled", show appointment dialog
    if (newStatus === 'Appointment Scheduled') {
      setAppointmentLeadId(leadId)
      setAppointmentLeadName(leadName)
      setAppointmentDialogOpen(true)
    } else {
      // Transform Title Case to snake_case for backend
      const transformedStatus = newStatus.toLowerCase().replace(/ /g, '_')
      router.patch(`/leads/${leadId}/update_status`, { status: transformedStatus }, {
        preserveScroll: true,
      })
    }
  }

  const handleCreateAppointment = () => {
    if (appointmentDate && appointmentTime && appointmentLeadId) {
      router.patch(`/leads/${appointmentLeadId}/update_status`, {
        status: 'Appointment Scheduled',
        appointment: {
          date: appointmentDate,
          time: appointmentTime,
          type: appointmentType,
        }
      }, {
        preserveScroll: true,
        onSuccess: () => {
          setAppointmentDialogOpen(false)
          setAppointmentLeadId(null)
          setAppointmentLeadName('')
          setAppointmentDate('')
          setAppointmentTime('')
          setAppointmentType('Diagnostic')
        }
      })
    }
  }

  const handleFilter = (filterType: string, value: string) => {
    router.get('/leads', {
      search: filters.search,
      status: filterType === 'status' ? (value !== 'all' ? value : undefined) : (filters.status !== 'all' ? filters.status : undefined),
      urgency: filterType === 'urgency' ? (value !== 'all' ? value : undefined) : (filters.urgency !== 'all' ? filters.urgency : undefined),
      source: filterType === 'source' ? (value !== 'all' ? value : undefined) : (filters.source !== 'all' ? filters.source : undefined),
      sort_by: sortBy || undefined,
      sort_direction: sortDirection || undefined,
    }, {
      preserveState: true,
      preserveScroll: true,
    })
  }

  const startEditingIssue = (e: React.MouseEvent, lead: Lead) => {
    e.stopPropagation()
    setEditingIssueId(lead.id)
    setEditingIssueValue(lead.issue_description || '')
  }

  const saveIssue = (leadId: number) => {
    const issueContent = editingIssueValue.trim()

    if (issueContent) {
      router.patch(`/leads/${leadId}`, { lead: { issue_description: issueContent } }, {
        preserveScroll: true,
      })
    }

    // Delay state clearing to allow click event to fully process
    // This prevents navigation when clicking away from the input
    setTimeout(() => {
      setEditingIssueId(null)
      setEditingIssueValue('')
    }, 0)
  }

  const cancelEditingIssue = () => {
    setEditingIssueId(null)
    setEditingIssueValue('')
  }

  const startEditingNote = (e: React.MouseEvent, lead: Lead) => {
    e.stopPropagation()
    setEditingNoteId(lead.id)
    // Backend sends notes ordered by created_at DESC (newest first)
    setEditingNoteValue(lead.notes.length > 0 ? lead.notes[0].content : '')
  }

  const saveNote = (leadId: number) => {
    const noteContent = editingNoteValue.trim()

    if (noteContent) {
      router.patch(`/leads/${leadId}/update_last_note`, { content: noteContent }, {
        preserveScroll: true,
      })
    }

    // Delay state clearing to allow click event to fully process
    // This prevents navigation when clicking away from the textarea
    setTimeout(() => {
      setEditingNoteId(null)
      setEditingNoteValue('')
    }, 0)
  }

  const cancelEditingNote = () => {
    setEditingNoteId(null)
    setEditingNoteValue('')
  }

  const startEditingFeedback = (e: React.MouseEvent, lead: Lead) => {
    e.stopPropagation()
    setEditingFeedbackId(lead.id)
    setEditingFeedbackValue(lead.feedback || '')
  }

  const saveFeedback = (leadId: number) => {
    const feedbackContent = editingFeedbackValue.trim()

    router.patch(`/leads/${leadId}`, { lead: { feedback: feedbackContent || null } }, {
      preserveScroll: true,
    })

    // Delay state clearing to allow click event to fully process
    setTimeout(() => {
      setEditingFeedbackId(null)
      setEditingFeedbackValue('')
    }, 0)
  }

  const cancelEditingFeedback = () => {
    setEditingFeedbackId(null)
    setEditingFeedbackValue('')
  }

  const formatStatus = (status: string) => {
    // Transform snake_case to Title Case
    return status
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }

  const getStatusColor = (status: string) => {
    // Accept both formats for color mapping
    const formattedStatus = formatStatus(status)
    switch (formattedStatus) {
      case 'New':
        return 'bg-blue-600 hover:bg-blue-700'
      case 'Contacted':
        return 'bg-purple-600 hover:bg-purple-700'
      case 'Estimate Requested':
        return 'bg-indigo-600 hover:bg-indigo-700'
      case 'Appointment Scheduled':
        return 'bg-orange-600 hover:bg-orange-700'
      case 'Won':
        return 'bg-green-600 hover:bg-green-700'
      case 'Lost':
        return 'bg-gray-600 hover:bg-gray-700'
      default:
        return 'bg-gray-600 hover:bg-gray-700'
    }
  }

  const statuses = ['New', 'Contacted', 'Estimate Requested', 'Appointment Scheduled', 'Won', 'Lost']

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    })
  }

  const getLastNote = (notes: Note[]) => {
    if (notes.length === 0) return 'No notes'
    // Backend sends notes ordered by created_at DESC (newest first)
    const lastNote = notes[0]
    return lastNote.content.length > 100 ? lastNote.content.substring(0, 100) + '...' : lastNote.content
  }

  const getSocialMediaId = (lead: Lead) => {
    // Instagram: show @instagram_username
    if (lead.source === 'Instagram' && lead.instagram_username) {
      return `@${lead.instagram_username}`
    }
    // Facebook Messenger: show @messenger_username
    if (lead.source === 'Facebook Messenger' && lead.messenger_username) {
      return `@${lead.messenger_username}`
    }
    // Other social media sources: show @source_id (as username handle)
    const socialSources = ['Telegram', 'WhatsApp', 'YouTube']
    if (socialSources.includes(lead.source) && lead.source_id) {
      return `@${lead.source_id}`
    }
    // Non-social sources: show —
    return '—'
  }

  const formatPhone = (phone: string | null) => {
    if (!phone) return '—'
    // Remove all non-numeric characters (dashes, spaces, parentheses, etc.)
    return phone.replace(/[^0-9]/g, '')
  }

  const SortableHeader = ({ column, children }: { column: string; children: React.ReactNode }) => {
    const isActive = sortBy === column
    const showUpArrow = isActive && sortDirection === 'asc'
    const showDownArrow = isActive && sortDirection === 'desc'

    return (
      <TableHead
        className="cursor-pointer select-none hover:bg-muted/50"
        onClick={() => handleSort(column)}
      >
        <div className="flex items-center gap-1">
          {children}
          {showUpArrow && <ArrowUpIcon className="size-4" />}
          {showDownArrow && <ArrowDownIcon className="size-4" />}
        </div>
      </TableHead>
    )
  }

  return (
    <>
      <div className="flex flex-1 flex-col min-w-0">
        <div className="@container/main flex flex-1 flex-col gap-2 min-w-0">
        <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6 min-w-0">
          {/* Header */}
          <div className="flex items-center justify-between px-4 lg:px-6">
            <div>
              <h1 className="text-3xl font-bold tracking-tight">Modern Shop Leads ({stats.total})</h1>
            </div>
            <Button
              size="sm"
              onClick={() => router.visit('/leads/new')}
              data-testid="create-lead-button"
              className="rounded-lg bg-[#5589ed] hover:bg-[#4478dc] text-white shadow-lg shadow-[#5589ed]/20"
            >
              <PlusIcon className="mr-2 size-4" />
              New Lead
            </Button>
          </div>

          {/* Filters and Table */}
          <div className="px-4 lg:px-6">
            <Card className="w-full min-w-0 rounded-2xl">
              <CardHeader>
                <CardTitle>Filter Leads</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Filters */}
                <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
                  <div className="space-y-2">
                    <Label htmlFor="search">Search by Name, Phone, or Company</Label>
                    <div className="relative">
                      <SearchIcon className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        id="search"
                        type="text"
                        placeholder="Search leads..."
                        value={searchTerm}
                        onChange={(e) => {
                          setSearchTerm(e.target.value)
                          handleSearch(e.target.value)
                        }}
                        className="pl-9 pr-9"
                      />
                      {searchTerm && (
                        <button
                          type="button"
                          onClick={() => {
                            setSearchTerm('')
                            handleSearch('')
                          }}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        >
                          <XIcon className="size-4" />
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="status-filter">Status</Label>
                    <Select
                      value={filters.status || 'all'}
                      onValueChange={(value) => handleFilter('status', value)}
                    >
                      <SelectTrigger id="status-filter">
                        <SelectValue placeholder="All Statuses" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Statuses</SelectItem>
                        {statuses.map((status) => (
                          <SelectItem key={status} value={status}>
                            {status}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="source-filter">Source</Label>
                    <Select
                      value={filters.source || 'all'}
                      onValueChange={(value) => handleFilter('source', value)}
                    >
                      <SelectTrigger id="source-filter">
                        <SelectValue placeholder="All Sources" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Sources</SelectItem>
                        <SelectItem value="Phone">Phone</SelectItem>
                        <SelectItem value="Instagram">Instagram</SelectItem>
                        <SelectItem value="Facebook Messenger">Facebook Messenger</SelectItem>
                        <SelectItem value="Telegram">Telegram</SelectItem>
                        <SelectItem value="WhatsApp">WhatsApp</SelectItem>
                        <SelectItem value="YouTube">YouTube</SelectItem>
                        <SelectItem value="Website">Website</SelectItem>
                        <SelectItem value="Referral">Referral</SelectItem>
                        <SelectItem value="Walk-in">Walk-in</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="urgency-filter">Created Date</Label>
                    <Select
                      value={filters.urgency || 'all'}
                      onValueChange={(value) => handleFilter('urgency', value)}
                    >
                      <SelectTrigger id="urgency-filter">
                        <SelectValue placeholder="Pick a date range" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Dates</SelectItem>
                        <SelectItem value="Emergency">Emergency</SelectItem>
                        <SelectItem value="High">High</SelectItem>
                        <SelectItem value="Medium">Medium</SelectItem>
                        <SelectItem value="Low">Low</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Table */}
                <div className="rounded-xl border">
                  <Table>
                    <TableHeader>
                      <TableRow className="hover:bg-transparent">
                        <SortableHeader column="source">Source</SortableHeader>
                        <SortableHeader column="social_media_id">Social Media ID</SortableHeader>
                        <SortableHeader column="phone">Phone</SortableHeader>
                        <SortableHeader column="name">Name</SortableHeader>
                        <SortableHeader column="status">Status</SortableHeader>
                        <SortableHeader column="sentiment">Sentiment</SortableHeader>
                        <SortableHeader column="quality">Quality</SortableHeader>
                        <SortableHeader column="issue">Issue</SortableHeader>
                        <SortableHeader column="feedback">Feedback</SortableHeader>
                        <SortableHeader column="last_note">Last Note</SortableHeader>
                        <SortableHeader column="updated_at">Last Updated</SortableHeader>
</TableRow>
                    </TableHeader>
                    <TableBody>
                      {leads.length > 0 ? (
                        leads.map((lead) => (
                          <TableRow
                            key={lead.id}
                            data-testid={`lead-row-${lead.id}`}
                          >
                            <TableCell
                              className="cursor-pointer hover:underline"
                              onClick={(e) => {
                                // Don't navigate if we're currently editing
                                if (editingIssueId === lead.id || editingNoteId === lead.id || editingFeedbackId === lead.id) {
                                  e.preventDefault()
                                  return
                                }
                                router.visit(`/leads/${lead.id}`)
                              }}
                            >
                              {lead.source}
                            </TableCell>
                            <TableCell
                              className="cursor-pointer hover:underline"
                              onClick={(e) => {
                                // Don't navigate if we're currently editing
                                if (editingIssueId === lead.id || editingNoteId === lead.id || editingFeedbackId === lead.id) {
                                  e.preventDefault()
                                  return
                                }
                                router.visit(`/leads/${lead.id}`)
                              }}
                            >
                              {getSocialMediaId(lead)}
                            </TableCell>
                            <TableCell
                              className="cursor-pointer hover:underline"
                              onClick={(e) => {
                                // Don't navigate if we're currently editing
                                if (editingIssueId === lead.id || editingNoteId === lead.id || editingFeedbackId === lead.id) {
                                  e.preventDefault()
                                  return
                                }
                                router.visit(`/leads/${lead.id}`)
                              }}
                            >
                              {formatPhone(lead.phone)}
                            </TableCell>
                            <TableCell
                              className="font-medium cursor-pointer hover:underline"
                              onClick={(e) => {
                                // Don't navigate if we're currently editing
                                if (editingIssueId === lead.id || editingNoteId === lead.id || editingFeedbackId === lead.id) {
                                  e.preventDefault()
                                  return
                                }
                                router.visit(`/leads/${lead.id}`)
}}
                            >
                              {lead.name}
                            </TableCell>
                            <TableCell>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Badge
                                    className={`${getStatusColor(lead.status)} rounded-full text-xs cursor-pointer`}
                                    data-testid={`status-badge-${lead.id}`}
                                  >
                                    {formatStatus(lead.status)}
                                  </Badge>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="start">
                                  {statuses.map((status) => (
                                    <DropdownMenuItem
                                      key={status}
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        handleStatusChange(lead.id, lead.name, status)
                                      }}
                                      data-testid={`status-option-${status.toLowerCase().replace(/\s+/g, '-')}`}
                                    >
                                      {status}
                                    </DropdownMenuItem>
                                  ))}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                            <TableCell>
                              {lead.sentiment ? (
                                <Badge
                                  variant={
                                    lead.sentiment === 'positive' ? 'default' :
                                    lead.sentiment === 'negative' ? 'destructive' :
                                    'secondary'
                                  }
                                  className="rounded-full text-xs capitalize"
                                >
                                  {lead.sentiment}
                                </Badge>
                              ) : (
                                <span className="text-muted-foreground text-xs">—</span>
                              )}
                            </TableCell>
                            <TableCell>
                              <Badge
                                variant={
                                  lead.quality === 'Good' ? 'default' :
                                  lead.quality === 'Bad' ? 'destructive' :
                                  'secondary'
                                }
                                className="rounded-full text-xs"
                              >
                                {lead.quality || 'Neutral'}
                              </Badge>
                            </TableCell>
                            <TableCell
                              className="max-w-xs"
                              onClick={(e) => {
                                if (editingIssueId === lead.id) {
                                  e.stopPropagation()
                                }
                              }}
                            >
                              {editingIssueId === lead.id ? (
                                <Input
                                  value={editingIssueValue}
                                  onChange={(e) => setEditingIssueValue(e.target.value)}
                                  onBlur={() => saveIssue(lead.id)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      saveIssue(lead.id)
                                    } else if (e.key === 'Escape') {
                                      cancelEditingIssue()
                                    }
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                  autoFocus
                                  data-testid={`edit-issue-input-${lead.id}`}
                                  className="h-8"
                                />
                              ) : (
                                <div
                                  className="truncate cursor-pointer hover:underline"
                                  onClick={(e) => startEditingIssue(e, lead)}
                                  data-testid={`issue-${lead.id}`}
                                >
                                  {lead.issue_description || <span className="text-muted-foreground">Add issue...</span>}
                                </div>
                              )}
                            </TableCell>
                            <TableCell
                              className="max-w-xs"
                              onClick={(e) => {
                                if (editingFeedbackId === lead.id) {
                                  e.stopPropagation()
                                }
                              }}
                            >
                              {editingFeedbackId === lead.id ? (
                                <Textarea
                                  value={editingFeedbackValue}
                                  onChange={(e) => setEditingFeedbackValue(e.target.value)}
                                  onBlur={() => saveFeedback(lead.id)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                      e.preventDefault()
                                      saveFeedback(lead.id)
                                    } else if (e.key === 'Escape') {
                                      cancelEditingFeedback()
                                    }
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                  autoFocus
                                  data-testid={`edit-feedback-textarea-${lead.id}`}
                                  placeholder="Add feedback..."
                                  className="min-h-[60px] text-sm"
                                  rows={2}
                                />
                              ) : (
                                <div
                                  className="truncate text-sm text-muted-foreground cursor-pointer hover:underline"
                                  onClick={(e) => startEditingFeedback(e, lead)}
                                  data-testid={`feedback-${lead.id}`}
                                >
                                  {lead.feedback || 'Add feedback...'}
                                </div>
                              )}
                            </TableCell>
                            <TableCell
                              className="max-w-xs"
                              onClick={(e) => {
                                if (editingNoteId === lead.id) {
                                  e.stopPropagation()
                                }
                              }}
                            >
                              {editingNoteId === lead.id ? (
                                <Textarea
                                  value={editingNoteValue}
                                  onChange={(e) => setEditingNoteValue(e.target.value)}
                                  onBlur={() => saveNote(lead.id)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' && e.ctrlKey) {
                                      saveNote(lead.id)
                                    } else if (e.key === 'Escape') {
                                      cancelEditingNote()
                                    }
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                  autoFocus
                                  data-testid={`edit-note-textarea-${lead.id}`}
                                  className="min-h-[60px] text-sm"
                                  rows={2}
                                />
                              ) : (
                                <div
                                  className="truncate text-sm text-muted-foreground cursor-pointer hover:underline"
                                  onClick={(e) => startEditingNote(e, lead)}
                                  data-testid={`last-note-${lead.id}`}
                                >
                                  {getLastNote(lead.notes)}
                                </div>
                              )}
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {formatDate(lead.updated_at)}
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={11} className="h-24 text-center">
                            <div className="flex flex-col items-center justify-center">
                              <p className="text-muted-foreground">
                                {filters.search || filters.status !== 'all' || filters.source !== 'all'
                                  ? 'No leads found. Try adjusting your filters.'
                                  : 'No leads yet. Create your first lead to get started.'}
                              </p>
                              {!filters.search && filters.status === 'all' && filters.source === 'all' && (
                                <Button
                                  onClick={() => router.visit('/leads/new')}
                                  className="mt-4 rounded-lg bg-[#5589ed] hover:bg-[#4478dc] text-white shadow-lg shadow-[#5589ed]/20"
                                >
                                  <PlusIcon className="mr-2 size-4" />
                                  Create Lead
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>

                {/* Footer */}
                {leads.length > 0 && (
                  <div className="space-y-2">
                    <div className="text-sm text-muted-foreground">
                      Showing 1 to {leads.length} of {stats.total} leads
                    </div>
                    <div className="text-xs text-muted-foreground italic">
                      Last Updated column displays times in Chicago Central Time
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      </div>

      {/* Appointment Quick-Create Dialog */}
    <Dialog open={appointmentDialogOpen} onOpenChange={setAppointmentDialogOpen}>
      <DialogContent data-testid="appointment-dialog">
        <DialogHeader>
          <DialogTitle>Schedule Appointment</DialogTitle>
          <DialogDescription>
            {appointmentLeadName ? `Create an appointment for ${appointmentLeadName}. ` : 'Create an appointment. '}
            This will update the lead status to "Appointment Scheduled".
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="appointment-date">Date *</Label>
            <Input
              id="appointment-date"
              type="date"
              value={appointmentDate}
              onChange={(e) => setAppointmentDate(e.target.value)}
              data-testid="appointment-date-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="appointment-time">Time *</Label>
            <Input
              id="appointment-time"
              type="time"
              value={appointmentTime}
              onChange={(e) => setAppointmentTime(e.target.value)}
              data-testid="appointment-time-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="appointment-type">Appointment Type *</Label>
            <Select value={appointmentType} onValueChange={setAppointmentType}>
              <SelectTrigger id="appointment-type" data-testid="appointment-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Diagnostic">Diagnostic</SelectItem>
                <SelectItem value="Repair">Repair</SelectItem>
                <SelectItem value="Inspection">Inspection</SelectItem>
                <SelectItem value="Maintenance">Maintenance</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => setAppointmentDialogOpen(false)}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateAppointment}
            disabled={!appointmentDate || !appointmentTime}
            className="bg-orange-600 hover:bg-orange-700"
            data-testid="create-appointment-button"
          >
            Create Appointment
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  )
}
